// Minimal JSON API client for the CRM web shell.
//
// - Always prefixes `/api/v1` so callers write domain paths (e.g. `/auth/me`).
// - Sends `credentials: 'include'` so the session cookie flows on same-origin
//   (Vite proxy) and cross-origin (prod) alike.
// - Rejects with a structured `ApiError` for non-2xx responses and surfaces
//   the backend's `{error, code?, details?}` envelope when present.
//
// TODO [WEEK-4]: add automatic retry + request cancellation (AbortController)
// once react-query adopts the full data layer.

const API_PREFIX = '/api/v1';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly details?: unknown;

  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (code !== undefined) {
      this.code = code;
    }
    if (details !== undefined) {
      this.details = details;
    }
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
  code?: string;
  details?: unknown;
}

async function parseErrorBody(res: Response): Promise<ErrorBody> {
  try {
    const data = (await res.json()) as ErrorBody;
    return data;
  } catch {
    return {};
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await parseErrorBody(res);
    const message = body.message ?? body.error ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body.code, body.details);
  }
  // 204 No Content or empty body → return undefined-ish cast to T.
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

function buildUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const normalised = path.startsWith('/') ? path : `/${path}`;
  return `${API_PREFIX}${normalised}`;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  return handleResponse<T>(res);
}

export async function apiPost<T, B = unknown>(path: string, body: B): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

export async function apiPatch<T, B = unknown>(path: string, body: B): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

export async function apiDelete<T = void>(path: string): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  return handleResponse<T>(res);
}
